import { Injectable } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { castArray, each } from "lodash-es";

@Injectable({
	providedIn: "root",
})
export class FormService {
	constructor(private formBuilder: UntypedFormBuilder) {}

	/**
	 * Add controls to an existing form group using Angular's `FormBuilder` API.
	 */
	addControls(group: UntypedFormGroup, controlsConfig: Record<string, any>): UntypedFormGroup {
		each(controlsConfig, (config, name) => {
			if (!name) {
				return;
			}

			group.addControl(
				name,
				config instanceof UntypedFormControl || config instanceof UntypedFormGroup
					? config
					: this.formBuilder.control.apply(this.formBuilder, castArray(config))
			);
		});
		return group;
	}

	/**
	 * Mark invalid controls as touched to trigger the validation errors.
	 * Won't touch `FormGroup` controls, only `FormControl` instances.
	 */
	touchInvalidControls(control: AbstractControl) {
		if (control.valid) {
			return;
		}

		if (control instanceof UntypedFormControl) {
			control.updateValueAndValidity();
			control.markAsTouched();
			return;
		}

		if (control instanceof UntypedFormGroup) {
			each(control.controls, (c) => this.touchInvalidControls(c));
		}
	}

	/**
	 * Build form group based on selection from Dictionaty of controls.
	 */
	buildFormGroup<T extends { [A in keyof T]: AbstractControl | undefined }, P extends { [A in keyof T]?: any }>(
		source: T,
		selection: P
	): UntypedFormGroup {
		const group = this.formBuilder.group({});
		for (const prop of Object.keys(selection)) {
			const control = (source as any)[prop];
			if (control) {
				group.addControl(prop, control);
			}
		}

		return group;
	}

	/**
	 * Remove validation from controls
	 * Uses it in case where you need/can skip of certain fields
	 * @param control
	 */
	clearControlValidation(control: AbstractControl) {
		control.clearValidators();
		control.updateValueAndValidity();
	}
}
